import {Form, Modal, Row, Col, DatePicker, Select, Switch, Input} from "antd";
import React, {useMemo, useState, useEffect} from "react";
import {useFormik} from "formik";
import {useTranslation} from "react-i18next";
import moment from "moment";
import {useStore} from "../../../../hooks/useStore";
import {ErrorMessage, SelectField} from "../ShiftEntryModal/ShiftEntryModalStyles";
import * as Yup from "yup";
import PoolShiftEntryDetailModal from "./PoolShiftEntryDetailModal";
import locale from "antd/es/date-picker/locale/tr_TR";
import {CourierDetailBox} from "../ShiftEntryModal/CourierDetailBox/CourierDetailBox";

const PoolShiftEntryModal = ({visible, onCancel, setIsModalOpen, setReloadData, poolData}) => {
    const {t} = useTranslation();
    const stores = useStore();
    const [courierId, setCourierId] = useState("");
    const [selectedPoolId, setSelectedPoolId] = useState("");
    const [selectedCourier, setSelectedCourier] = useState(null);
    const [poolShiftForecast, setPoolShiftForecast] = useState(null);
    const [isJoker, setIsJoker] = useState(false);
    const [jokerDistance, setJokerDistance] = useState(1000);

    const formValidation = Yup.object().shape({
        date: Yup.string().required(t("validation.required", {ns: "common"})),
        courier: Yup.string().required(t("validation.required", {ns: "common"})),
        pool: Yup.string().required(t("validation.required", {ns: "common"})),
        shiftStartTime: Yup.string().required(t("validation.required", {ns: "common"})),
        shiftEndTime: Yup.string().required(t("validation.required", {ns: "common"}))
    });

    // Seçilen havuza göre kuryeler
    const availableCouriers = useMemo(() => {
        if (!selectedPoolId || !poolData?.data) {
            return [];
        }

        const selectedPool = poolData.data.find(pool => pool._id === selectedPoolId);
        return selectedPool?.couriers || [];
    }, [selectedPoolId, poolData]);

    useEffect(() => {
        if (visible) {
            setSelectedPoolId("");
            setCourierId("");
            setSelectedCourier(null);
            setIsJoker(false);
            setJokerDistance(1000);
            setPoolShiftForecast(null);
            formik.resetForm();
        }
    }, [visible]);

    const formik = useFormik({
        initialValues: {
            date: "",
            courier: "",
            pool: "",
            shiftStartTime: null,
            shiftEndTime: null
        },
        validationSchema: formValidation,
        enableReinitialize: true,
        onSubmit: async () => {
            const poolShift = {
                date: moment(formik.values.date).format("YYYY-MM-DD"),
                poolId: selectedPoolId,
                courierId: courierId,
                startHourId: values.shiftStartTime,
                endHourId: values.shiftEndTime,
                fromShiftManagement: true,
                isJoker: isJoker,
                jokerDistance: jokerDistance
            };
            await stores.poolShiftStore.bookMultipleSlots(poolShift);
            setReloadData();
            setIsModalOpen(false);
            formik.resetForm();
        }
    });

    const {values, setFieldValue, handleSubmit, errors, touched} = formik;

    const handlePoolChange = poolId => {
        setSelectedPoolId(poolId);
        setFieldValue("pool", poolId);
        setFieldValue("courier", "");
        setCourierId("");
        setSelectedCourier(null);
    };

    // Kurye değiştiğinde seçilen kuryeyi güncelle
    const handleCourierChange = (value, option) => {
        const selectedCourierData = availableCouriers.find(courier => courier._id === value);
        setCourierId(value);
        setSelectedCourier(selectedCourierData);
        setFieldValue("courier", value);
    };

    const handleDateChange = async date => {
        const poolShiftForecast = await stores.poolShiftStore.getPoolShiftForecastByDate({
            date: moment(date).format("YYYY-MM-DD"),
            poolId: selectedPoolId
        });
        setPoolShiftForecast(poolShiftForecast);
        setFieldValue("date", date);
    };

    const handleShiftStartTimeChange = shiftStartTime => {
        setFieldValue("shiftStartTime", shiftStartTime);
    };

    const handleShiftEndTimeChange = shiftEndTime => {
        setFieldValue("shiftEndTime", shiftEndTime);
    };

    const handleJokerCourierChange = checked => {
        setIsJoker(checked);
    };

    const handleJokerCourierDistanceChange = e => {
        setJokerDistance(e.target.value);
    };

    const timeDiffMinutes = () => {
        // Seçilen vardiya saatlerinin string değerlerini al
        const startTimeStr = poolShiftForecast?.data?.selectableHours?.find(
            shift => shift._id === values.shiftStartTime
        )?.startTime;

        const endTimeStr = poolShiftForecast?.data?.selectableHours?.find(
            shift => shift._id === values.shiftEndTime
        )?.endTime;

        if (!startTimeStr || !endTimeStr) {
            return 0;
        }

        const timeToMinutes = timeStr => {
            const [hours, minutes] = timeStr.split(":").map(Number);
            return hours * 60 + minutes;
        };

        let startMinutes = timeToMinutes(startTimeStr);
        let endMinutes = timeToMinutes(endTimeStr);

        // gün geçişi
        if (endMinutes < startMinutes) {
            endMinutes += 24 * 60;
        }

        return endMinutes - startMinutes;
    };

    return (
        <Modal
            centered
            width={920}
            visible={visible}
            onOk={handleSubmit}
            onCancel={onCancel}
            okText={t("modal.saveButton", {ns: "common"})}
            cancelText={t("modal.cancelButton2", {ns: "common"})}
        >
            <Form>
                <Row>
                    <Col span={11}>
                        <Form.Item>
                            <h4>{t("fields.pool", {ns: "common"})}</h4>
                            <Select
                                allowClear
                                showSearch
                                value={selectedPoolId}
                                onChange={handlePoolChange}
                                options={poolData?.data?.map(pool => {
                                    return {
                                        label: pool.name,
                                        value: pool._id
                                    };
                                })}
                            />
                            {errors.pool && touched.pool ? (
                                <ErrorMessage>{errors.pool}</ErrorMessage>
                            ) : null}
                        </Form.Item>
                        <Form.Item>
                            <h4>{t("fields.date", {ns: "common"})}</h4>
                            <DatePicker
                                locale={locale}
                                value={values.date}
                                format={"DD.MM.YYYY"}
                                onSelect={handleDateChange}
                                disabled={!selectedPoolId}
                                disabledDate={current => {
                                    return current && current <= moment().subtract(1, "day");
                                }}
                            />
                            {errors.date && touched.date ? (
                                <ErrorMessage>{errors.date}</ErrorMessage>
                            ) : null}
                        </Form.Item>
                        <Form.Item>
                            <h4>{t("fields.courier", {ns: "common"})}</h4>
                            <SelectField
                                allowClear
                                showSearch
                                disabled={!selectedPoolId}
                                value={values.courier}
                                onChange={handleCourierChange}
                                placeholder="Kurye seçin..."
                                filterOption={(input, option) => {
                                    const courier = availableCouriers.find(
                                        c => c._id === option.value
                                    );
                                    if (!courier) return false;

                                    const searchText = input.toLowerCase();
                                    const fullname = (courier.fullname || "")
                                        .toString()
                                        .toLowerCase();
                                    const tckn = (courier.tckn || "").toString().toLowerCase();
                                    const cellPhone = (courier.cellPhone || "")
                                        .toString()
                                        .toLowerCase();

                                    return (
                                        fullname.includes(searchText) ||
                                        tckn.includes(searchText) ||
                                        cellPhone.includes(searchText)
                                    );
                                }}
                            >
                                {availableCouriers.map(courier => (
                                    <SelectField.Option
                                        key={courier._id}
                                        value={courier._id}
                                        data-testid="courierSelect_option"
                                    >
                                        <CourierDetailBox
                                            courierName={courier.fullname}
                                            courierTckn={courier.tckn}
                                            courierCellPhone={courier.cellPhone}
                                        />
                                    </SelectField.Option>
                                ))}
                            </SelectField>
                            {errors.courier && touched.courier ? (
                                <ErrorMessage>{errors.courier}</ErrorMessage>
                            ) : null}
                        </Form.Item>
                        <Form.Item>
                            <h4>{t("shiftScore.shiftStartTime", {ns: "shiftScore"})}</h4>
                            <Select
                                value={values.shiftStartTime}
                                options={poolShiftForecast?.data?.selectableHours?.map(shift => {
                                    return {
                                        label: shift.startTime,
                                        value: shift._id
                                    };
                                })}
                                onChange={handleShiftStartTimeChange}
                                disabled={!poolShiftForecast}
                                allowClear
                            />

                            {errors.shiftStartTime && touched.shiftStartTime ? (
                                <ErrorMessage>{errors.shiftStartTime}</ErrorMessage>
                            ) : null}
                        </Form.Item>
                        <Form.Item>
                            <h4>{t("shiftScore.shiftEndTime", {ns: "shiftScore"})}</h4>
                            <Select
                                value={values.shiftEndTime}
                                options={poolShiftForecast?.data?.selectableHours?.map(shift => {
                                    return {
                                        label: shift.endTime,
                                        value: shift._id
                                    };
                                })}
                                onChange={handleShiftEndTimeChange}
                                disabled={!poolShiftForecast}
                                allowClear
                            />

                            {errors.shiftEndTime && touched.shiftEndTime ? (
                                <ErrorMessage>{errors.shiftEndTime}</ErrorMessage>
                            ) : null}
                        </Form.Item>

                        <Row gutter={16}>
                            <Col span={8}>
                                <Form.Item>
                                    <h4>{t("shiftScore.jokerCourier", {ns: "shiftScore"})}</h4>
                                    <Switch checked={isJoker} onChange={handleJokerCourierChange} />
                                </Form.Item>
                            </Col>
                            <Col span={16}>
                                <Form.Item>
                                    <h4>
                                        {t("shiftScore.jokerCourierDistance", {ns: "shiftScore"})}
                                    </h4>
                                    <Input
                                        value={jokerDistance}
                                        onChange={handleJokerCourierDistanceChange}
                                        disabled={!isJoker}
                                        type="number"
                                        min={0}
                                        style={{width: "100%"}}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                    </Col>
                    <Col offset={1} span={12}>
                        <PoolShiftEntryDetailModal
                            poolForecastDate={
                                values.date && moment(values.date).format("DD.MM.YYYY")
                            }
                            fullname={selectedCourier?.fullname || ""}
                            tckn={selectedCourier?.tckn || ""}
                            cellPhone={selectedCourier?.cellPhone || ""}
                            poolName={
                                selectedPoolId && poolData?.data
                                    ? poolData.data.find(pool => pool._id === selectedPoolId)?.name
                                    : ""
                            }
                            shiftStartTime={
                                poolShiftForecast?.data?.selectableHours?.find(
                                    shift => shift._id === values.shiftStartTime
                                )?.startTime
                            }
                            shiftEndTime={
                                poolShiftForecast?.data?.selectableHours?.find(
                                    shift => shift._id === values.shiftEndTime
                                )?.endTime
                            }
                            workTimeMinute={timeDiffMinutes() !== NaN ? timeDiffMinutes() : 0}
                        />
                    </Col>
                </Row>
            </Form>
        </Modal>
    );
};

export default PoolShiftEntryModal;
